import { Comment, DirectMessage, Post, Report, ReportReason, User } from 'model';
import { BaseApi } from './BaseAPI';

const baseURL = 'report/';
export const ReportApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		/**
		 * Get reports for the comment, reported by me
		 */
		getCommentReports: builder.query<Report, Comment['_id'] | undefined>({
			query: document => ({
				url: `${baseURL}comment/${document}/`
			}),
			providesTags: (...args) => [{ type: 'Comment', id: args[2] }]
		}),
		/**
		 * Get reports for the post, reported by me
		 */
		getPostReports: builder.query<Report, Post['_id'] | undefined>({
			query: document => ({
				url: `${baseURL}post/${document}/`
			}),
			providesTags: (...args) => [{ type: 'Post', id: args[2] }]
		}),

		/**
		 * Get reports for the direct message, reported by me
		 */
		getDirectMessageReports: builder.query<Report, Post['_id'] | undefined>({
			query: document => ({
				url: `${baseURL}directMessage/${document}/`
			}),
			providesTags: (...args) => [{ type: 'Post', id: args[2] }]
		}),

		/**
		 * Report a comment
		 */
		reportComment: builder.mutation<Report, { document: Comment['_id']; reason: ReportReason }>({
			query: ({ document, reason }) => ({
				url: `${baseURL}`,
				method: 'PUT',
				body: { document, type: 'comment', reason }
			}),
			invalidatesTags: (...args) => [{ type: 'Comment', id: args[2].document }]
		}),
		/**
		 * Report a post, goes to the same endpoint as comment, just a type wrapper
		 */
		reportPost: builder.mutation<Report, { document: Post['_id']; reason: ReportReason }>({
			query: ({ document, reason }) => ({
				url: `${baseURL}`,
				method: 'PUT',
				body: { document, type: 'post', reason }
			}),
			invalidatesTags: (...args) => [{ type: 'Post', id: args[2].document }]
		}),
		/**
		 * Report a direct message, goes to the same endpoint as comment, just a type wrapper
		 */
		reportDirectMessage: builder.mutation<Report, { document: DirectMessage['_id']; reason: ReportReason }>({
			query: ({ document, reason }) => ({
				url: `${baseURL}`,
				method: 'PUT',
				body: { document, type: 'directMessage', reason }
			}),
			invalidatesTags: (...args) => [{ type: 'Direct Message', id: args[2].document }]
		}),

		/**
		 * Unreport a comment by ID
		 */
		unReportComment: builder.mutation<Report, { document: Comment['_id'] }>({
			query: ({ document }) => ({
				url: `${baseURL}`,
				method: 'DELETE',
				body: { document, type: 'comment' }
			}),
			invalidatesTags: (...args) => [{ type: 'Comment', id: args[2].document }]
		}),

		/**
		 * Unreport a post by ID
		 */
		unReportPost: builder.mutation<Report, { document: Post['_id'] }>({
			query: ({ document }) => ({
				url: `${baseURL}`,
				method: 'DELETE',
				body: { document, type: 'post' }
			}),
			invalidatesTags: (...args) => [{ type: 'Post', id: args[2].document }]
		}),

		/**
		 * Unreport a direct message by ID
		 */
		unReportDirectMessage: builder.mutation<Report, { document: Comment['_id'] }>({
			query: ({ document }) => ({
				url: `${baseURL}`,
				method: 'DELETE',
				body: { document, type: 'directMessage' }
			}),
			invalidatesTags: (...args) => [{ type: 'Comment', id: args[2].document }]
		}),

		getReportsByDocument: builder.query<Report[], { document: string }>({
			query: ({ document }) => ({
				url: `${baseURL}${document}`
			}),
			providesTags: [{ type: 'Report' }]
		}),

		getReportsByUser: builder.query<Report[], { user: User['_id'] }>({
			query: ({ user }) => ({
				url: `${baseURL}user/${user}`
			}),
			providesTags: [{ type: 'Report' }]
		}),

		getReportsByReporter: builder.query<Report[], { reporter: User['_id'] }>({
			query: ({ reporter }) => ({
				url: `${baseURL}reporter/${reporter}`
			}),
			providesTags: [{ type: 'Report' }]
		})
	})
});

export const {
	useReportCommentMutation,
	useReportPostMutation,
	useUnReportCommentMutation,
	useUnReportPostMutation,
	useGetCommentReportsQuery,
	useGetPostReportsQuery,
	useGetReportsByDocumentQuery,
	useGetReportsByUserQuery,
	useGetReportsByReporterQuery,
	useReportDirectMessageMutation,
	useUnReportDirectMessageMutation,
	useGetDirectMessageReportsQuery
} = ReportApi;
